import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bot, Send, Mic, MicOff, RotateCcw, X } from 'lucide-react';
import { useEmbedAuth, getToken as embedGetToken } from '../utils/embedAuth';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AgentEmbedPage = ({ agentId: propAgentId, onClose }) => {
  const params = new URLSearchParams(window.location.search);
  const agentId = propAgentId || params.get('agent_id');
  const isIframe = !propAgentId;

  // Sesión persistente cross-iframe — se mantiene aunque el usuario abra
  // otra ventana en el mismo dominio donde OkBot esté incrustado.
  const { user: embedUser } = useEmbedAuth();

  const [agent, setAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const endRef = useRef(null);
  const recognitionRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!agentId) { setError('No se especificó un agente'); return; }
    loadAgent();
    setupSpeech();
  }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for global voice trigger from external mic FABs.
  // Also drains a pre-mount pending intent stored on window (handshake).
  useEffect(() => {
    const startNow = () => {
      if (!recognitionRef.current) return false;
      try {
        if (!listening) { recognitionRef.current.start(); setListening(true); }
        return true;
      } catch (_) { return false; }
    };
    const handler = (e) => {
      const targetId = e?.detail?.agentId;
      if (targetId && targetId !== agentId) return;
      startNow();
    };
    window.addEventListener('okbot:start-voice', handler);
    // Drain pending intent set before this embed mounted
    try {
      const pending = window.__okbotPendingVoice;
      if (pending && (!pending.agentId || pending.agentId === agentId)) {
        if (Date.now() - (pending.ts || 0) < 5000) {
          // Wait one tick for setupSpeech to finish
          setTimeout(() => { if (startNow()) { delete window.__okbotPendingVoice; } }, 50);
        }
      }
    } catch (_) {}
    // Announce readiness so external triggers can fire reliably
    try { window.dispatchEvent(new CustomEvent('okbot:voice-ready', { detail: { agentId } })); } catch (_) {}
    return () => window.removeEventListener('okbot:start-voice', handler);
  }, [agentId, listening]);

  const loadAgent = async () => {
    try {
      const res = await axios.get(`${API}/admin/ai-agents/${agentId}`);
      const a = res.data;
      setAgent(a);
      setMessages([{
        role: 'assistant',
        content: a.greeting || `¡Hola! Soy ${a.name}. ¿En qué puedo ayudarte?`,
        ts: Date.now()
      }]);
    } catch {
      setError('No se pudo cargar el agente. Verifica la configuración.');
    }
  };

  const setupSpeech = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      const rec = new SR();
      rec.lang = 'es-MX';
      rec.continuous = false;
      rec.interimResults = false;
      rec.onresult = (e) => { setInput(p => p + e.results[0][0].transcript); setListening(false); };
      rec.onerror = () => setListening(false);
      rec.onend = () => setListening(false);
      recognitionRef.current = rec;
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading || !agent) return;
    const msg = input.trim();
    setInput('');
    const userMsg = { role: 'user', content: msg, ts: Date.now() };
    setMessages(p => [...p, userMsg]);
    const newHistory = [...history, { role: 'user', content: msg }];
    setLoading(true);
    try {
      const token = embedGetToken();
      const res = await axios.post(`${API}/admin/ai-agents/${agentId}/chat`, {
        message: msg,
        history: history.slice(-12),
        user_id: embedUser?.id || embedUser?.okuser || 'embed-user',
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true });
      const reply = res.data.response || res.data.message || 'Sin respuesta';
      setMessages(p => [...p, { role: 'assistant', content: reply, ts: Date.now() }]);
      setHistory([...newHistory, { role: 'assistant', content: reply }]);
    } catch {
      setMessages(p => [...p, {
        role: 'assistant',
        content: 'Lo siento, hubo un error al procesar tu mensaje. Por favor intenta de nuevo.',
        ts: Date.now(),
        isError: true
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const toggleVoice = () => {
    if (!recognitionRef.current) return;
    if (listening) { recognitionRef.current.stop(); setListening(false); }
    else { recognitionRef.current.start(); setListening(true); }
  };

  const resetChat = () => {
    setHistory([]);
    setMessages(agent ? [{
      role: 'assistant',
      content: agent.greeting || `¡Hola! Soy ${agent.name}. ¿En qué puedo ayudarte?`,
      ts: Date.now()
    }] : []);
  };

  const agentColor = agent?.color || '#ef4444';

  if (error) {
    return (
      <div style={{ height: '100%', background: '#0d0d0f', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <Bot size={40} style={{ color: '#4b5563', marginBottom: 12 }} />
        <p style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center' }}>{error}</p>
      </div>
    );
  }

  if (!agent) {
    return (
      <div style={{ height: '100%', background: '#0d0d0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 24, height: 24, border: `2px solid ${agentColor}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{
      height: '100%', width: '100%', display: 'flex', flexDirection: 'column',
      background: '#0d0d0f', fontFamily: "'Inter', system-ui, sans-serif", overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(255,255,255,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 10,
            background: `${agentColor}25`, border: `1px solid ${agentColor}40`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <img src="/okbot_logo_2026.png" alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
          </div>
          <div>
            <p style={{ color: '#fff', fontWeight: 600, fontSize: 13, margin: 0 }}>{agent.name}</p>
            <p style={{ color: '#4ade80', fontSize: 10, margin: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
              En línea
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={resetChat} style={{ padding: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', borderRadius: 6 }} title="Reiniciar chat">
            <RotateCcw size={14} />
          </button>
          {onClose && (
            <button onClick={onClose} style={{ padding: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', borderRadius: 6 }} title="Cerrar">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            {msg.role === 'assistant' && (
              <div style={{
                width: 26, height: 26, borderRadius: '50%', flexShrink: 0, marginRight: 8, marginTop: 2,
                background: `${agentColor}30`, display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <img src="/okbot_logo_2026.png" alt="" style={{ width: 13, height: 13, objectFit: 'contain' }} />
              </div>
            )}
            <div style={{
              maxWidth: '78%', padding: '8px 12px', borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
              background: msg.role === 'user' ? agentColor : msg.isError ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.07)',
              color: msg.role === 'user' ? '#fff' : msg.isError ? '#fca5a5' : '#e5e7eb',
              fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word'
            }}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
              background: `${agentColor}30`, display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <img src="/okbot_logo_2026.png" alt="" style={{ width: 13, height: 13, objectFit: 'contain' }} />
            </div>
            <div style={{ background: 'rgba(255,255,255,0.07)', padding: '8px 14px', borderRadius: '14px 14px 14px 4px', display: 'flex', gap: 4 }}>
              {[0,1,2].map(d => (
                <span key={d} style={{ width: 6, height: 6, borderRadius: '50%', background: '#6b7280', display: 'inline-block', animation: 'bounce 1s infinite', animationDelay: `${d*0.15}s` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Escribe un mensaje..."
            disabled={loading}
            rows={1}
            style={{
              flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 10, padding: '8px 12px', color: '#fff', fontSize: 13, resize: 'none',
              outline: 'none', maxHeight: 80, overflowY: 'auto', fontFamily: 'inherit',
            }}
          />
          {recognitionRef.current && (
            <button onClick={toggleVoice} style={{
              padding: '8px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: listening ? agentColor : 'rgba(255,255,255,0.08)',
              color: listening ? '#fff' : '#9ca3af', flexShrink: 0
            }}>
              {listening ? <MicOff size={15} /> : <Mic size={15} />}
            </button>
          )}
          <button onClick={sendMessage} disabled={loading || !input.trim()} style={{
            padding: '8px 12px', borderRadius: 10, border: 'none', cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
            background: agentColor, color: '#fff', opacity: loading || !input.trim() ? 0.4 : 1,
            display: 'flex', alignItems: 'center', flexShrink: 0, transition: 'opacity 0.2s'
          }}>
            <Send size={15} />
          </button>
        </div>
        <p style={{ color: '#374151', fontSize: 10, textAlign: 'center', margin: '6px 0 0' }}>
          Powered by OkBot IA
        </p>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
        textarea::placeholder { color: #4b5563; }
      `}</style>
    </div>
  );
};

export default AgentEmbedPage;
